// Shared constants + colour helpers for the Branch Calendar.
//
// Kept in a plain module (no React imports) so both the DayGrid and the
// WeekStaffGrid can share the same palette without pulling the whole
// BranchCalendar file into their bundle.

export const STATUS_HSL = {
  booked: "var(--status-booked)",
  in_progress: "var(--status-progress)",
  completed: "var(--status-completed)",
  cancelled: "var(--status-cancelled)",
  no_show: "var(--status-blocked)",
};

// Operator-friendly colour code for the appointment blocks. Priority (top wins,
// per Iter-344 spec: order status = highest, then check-in state, then deposit, then default):
//   1. completed (order Closed via "Close Order")         → dark grey
//      Wins over everything else — a settled booking is done.
//      Payment-only signals (order_payment_status === "paid") do NOT
//      trigger dark grey — a fully paid but still-OPEN order stays
//      teal (if the guest checked in) until the operator explicitly
//      clicks Close Order.
//   2. no_show                                            → light grey
//   3. locked_to_staff                                    → red (still open, staff locked)
//   4. checked_in_at (front-desk check-in complete, still open) → bright teal
//   5. has_deposit / partially or fully paid, still open  → light green + Rp badge
//   6. booked / default                                   → pink
// Blue is reserved for lunch blocks (see LUNCH_COLOR below).
export const APPT_COLORS = {
  pink:       { bg: "#EBAFC0", text: "#3B0B18" },
  teal:       { bg: "#14B8A6", text: "#053B33" },   // guest checked in, tile still open
  lightGreen: { bg: "#dcfce7", text: "#14532d" },   // deposit received, still open
  red:        { bg: "#C0392B", text: "#FFFFFF" },
  lightGrey:  { bg: "#C7C7C7", text: "#333333" },
  darkGrey:   { bg: "#4B4B4B", text: "#FFFFFF" },
};

export const LUNCH_COLOR = { bg: "#B8D2E8", text: "#123048" }; // soft blue

// Appointments sitting under an off-duty / inactive practitioner render grey
// so the operator instantly sees they need dragging to an available column.
export const OFF_DUTY_APPT_COLOR = { bg: "#BFBFBF", text: "#2F2F2F" };

export function colorForAppt(a) {
  // Iter-255 · Transportation "Picked Up" — dark grey completion
  // status that never touches the order/close pipeline. Fires ONLY
  // when the appointment is a transportation tile; the backend
  // rejects `picked_up` on any other booking so we don't need to
  // dual-check here.
  if (a.status === "picked_up") return APPT_COLORS.darkGrey;
  // Priority 0 — Linked ORDER is Closed via "Close Order" (Iter-344
  // decouples payment from status): whenever a tile's invoice has been
  // EXPLICITLY closed by an operator, paint Dark Gray no matter what
  // the appointment's own `status` says. This still fixes the
  // visitcocoon.com regression where re-inserting a specialist on a
  // closed order reverted the appointment.status to `booked` — a
  // closed order still wins over `booked`.
  //
  // IMPORTANT (Iter-344): `order_payment_status === "paid"` is
  // DELIBERATELY NOT part of this check. A fully paid but still-OPEN
  // order must stay teal (if the guest is checked in), light-green
  // (deposit received), or pink (default) — only the manual
  // "Close Order" click can transition the tile to Dark Gray. This
  // preserves the ability to keep editing the treatment / adding line
  // items after a full deposit has been taken.
  const linkedOrderClosed = a.order_status === "completed" || a.order_status === "closed";
  if (linkedOrderClosed) return APPT_COLORS.darkGrey;
  // Priority 1 — Own status Closed (unchanged): a settled booking is done.
  if (a.status === "completed") return APPT_COLORS.darkGrey;
  if (a.status === "no_show") return APPT_COLORS.lightGrey;
  if (a.locked_to_staff) return APPT_COLORS.red;
  // Priority 2 — Guest has walked in and completed Front-Desk Check-In
  // (`checked_in_at` stamped by /auth/customer/checkin/complete on
  // every booked/in_progress appointment they hold today). Wins over
  // the deposit tint because it's a stronger operational signal:
  // the guest is on-site right now.
  if (a.checked_in_at) return APPT_COLORS.teal;
  // Priority 3 — Deposit received (partial or full) but the invoice is
  // still OPEN — waiting on the operator to click Close Order. Also
  // honours the legacy `deposit_paid` field seeded by earlier flows.
  if (a.has_deposit || Number(a.deposit_paid ?? a.deposit ?? 0) > 0) {
    return APPT_COLORS.lightGreen;
  }
  // Default — Unpaid / Booked.
  return APPT_COLORS.pink;
}

export const WEEKDAY_KEY = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
export const SLOT_MIN = 15;   // 15-minute rows
export const SLOT_PX = 20;    // 20 px per 15-min slot => 80 px per hour

export const DAY_OFF_MODES = new Set(["off", "day_off", "training_day", "closed"]);

/**
 * Group-glow palette used by the Booking Calendar.
 *
 * Spec: Group 1 → Blue, Group 2 → Green, Group 3 → Purple, Group 4 → Red.
 * We cycle through the four colours for any 5th+ group so the whole day
 * always has a colour, and the palette is stable per group_id within a
 * single render.
 *
 * `ring` is the RGBA used inside `box-shadow` — kept at ~55% alpha so the
 * glow reads as a soft blur rather than a hard outline. `label` powers
 * the visual legend on the Calendar Sidebar.
 */
export const GROUP_GLOW_PALETTE = [
  { key: "blue",   label: "Group 1", ring: "rgba(59, 130, 246, 0.55)" },   // sky-500
  { key: "green",  label: "Group 2", ring: "rgba(16, 185, 129, 0.55)" },   // emerald-500
  { key: "purple", label: "Group 3", ring: "rgba(139, 92, 246, 0.55)" },   // violet-500
  { key: "red",    label: "Group 4", ring: "rgba(239, 68, 68, 0.55)" },    // red-500
];

/**
 * Build a stable group_id → palette-entry map from the visible appointments.
 * Groups are ordered by their earliest tile's `start_iso` so the "first
 * group of the day" is always Blue, the second is always Green, etc.
 *
 * Only appointments with a truthy `group_id` are considered — this is the
 * appointment-group-booking id (multi-guest reservations), NOT the
 * `link_group_id` used for linked package chains.
 */
export function buildGroupPalette(appts) {
  if (!Array.isArray(appts) || appts.length === 0) return {};
  const firstSeen = new Map();
  for (const a of appts) {
    const gid = a && a.group_id;
    if (!gid) continue;
    const ts = a.start_iso || "";
    const prev = firstSeen.get(gid);
    if (!prev || ts < prev) firstSeen.set(gid, ts);
  }
  const ordered = [...firstSeen.entries()].sort((a, b) => (a[1] || "").localeCompare(b[1] || ""));
  const out = {};
  ordered.forEach(([gid], idx) => {
    out[gid] = GROUP_GLOW_PALETTE[idx % GROUP_GLOW_PALETTE.length];
  });
  return out;
}
